import React, { useState, useEffect } from 'react';
import { graphql } from "gatsby";
import { navigate } from "gatsby";
import { animate } from 'framer-motion';

// component imports
import Layout from '../../components/layout/Layout';
import Header from '../../components/layout/Header';

// nomination step imports
import Step1 from '../../components/forms/nomination/Step1';
import Step2 from '../../components/forms/nomination/Step2';
import Step3 from '../../components/forms/nomination/Step3';
import Step4 from '../../components/forms/nomination/Step4';
import Summary from '../../components/forms/nomination/Summary';
import ProgressIndicator from '../../components/forms/nomination/ProgressIndicator';
import CustomDialog from '../../components/forms/nomination/CustomDialog'; // Import Dialog component

const Invite = ({ data }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    step1: {},
    step2: {},
    step3: {},
    step4: {},
  });
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [dialogVisible, setDialogVisible] = useState(false); // State to manage dialog visibility
  const [dialogMessage, setDialogMessage] = useState(""); // State to manage dialog message
  const [dialogTitle, setDialogTitle] = useState(""); // State to manage dialog title
  const [isSuccess, setIsSuccess] = useState(false); // State to check if success
  const [resetOption, setResetOption] = useState(null); // State to store reset function

  const scrollToTop = () => {
    animate(window.scrollY, 0, {
      type: "spring",
      damping: 10,
      stiffness: 300,
      mass: 0.1,
      onUpdate: (value) => window.scrollTo(0, value),
    });
  };

  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  useEffect(() => {
    if (dialogVisible) {
      scrollToTop();
    }
  }, [dialogVisible]);

  const handleNext = (data, newFiles) => {
    setFormData((prev) => ({ ...prev, [`step${currentStep}`]: data }));
    if (newFiles) {
      setFormData((prev) => ({ ...prev, step3: { ...prev.step3, files: newFiles } }));
    }
    if (currentStep === 1) {
      // Validate and show dialog for Step 1
      const errors = validateStep1(data);
      if (Object.keys(errors).length === 0) {
        setDialogTitle("Super! Deine nominierte Person erfüllt die Voraussetzungen für die Nominierung für den Movers of Tomorrow Award");
        setDialogMessage("");
        setIsSuccess(true);
      } else {
        setDialogTitle("Kriterien nicht erfüllt! Die Nominierung kann leider nicht fortgesetzt werden, da nicht alle notwendigen Kriterien erfüllt sind. Bitte überprüfe, ob folgende Bedingungen zutreffen:");
        setDialogMessage(
          <div className="html-content">
            <ul className="flex flex-col gap-y-2">
              {Object.keys(errors).map((key) => (
                <li key={key} className="">{errors[key]}</li>
              ))}
            </ul>
          </div>
        );
        setIsSuccess(false);
      }
      setDialogVisible(true);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const validateStep1 = (data) => {
    const errors = {};
    if (!data.age) errors.age = 'Ist die nominierte Person zwischen 16 und 24 Jahre alt?';
    if (!data.inGermany) errors.inGermany = 'Engagiert sich die nominierte Person in Deutschland?';
    if (!data.nomineeKnows) errors.nomineeKnows = 'Hast du das Einverständnis der nominierten Person?';
    if (!data.selectedOption) errors.selectedOption = 'In welchem Bereich engagiert sich die nominierte Person?';
    if (data.selectedOption === 'Other') errors.selectedOption = 'Leider können wir nur Kandidatinnen akzeptieren, die in einen der vorher beschriebenen Bereiche fallen.';
    return errors;
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = (data) => {
    setFormData((prev) => ({ ...prev, step4: data }));
    setCurrentStep((prev) => prev + 1); // Move to summary
  };

  const abort = () => {
    setFormData({
      step1: {},
      step2: {},
      step3: {},
      step4: {},
    });
    setCurrentStep(1);
    navigate('/');
  };

  const handleFinalSubmit = async () => {
    try {
      // Check file sizes first - Airtable has a ~5MB limit per request
      const MAX_AIRTABLE_SIZE = 5 * 1024 * 1024; // 5MB
      const filesTooLarge = files.filter(file => file.size > MAX_AIRTABLE_SIZE);

      if (filesTooLarge.length > 0) {
        alert(`Einige Dateien sind zu groß für den direkten Upload (max. 5MB): ${filesTooLarge.map(f => f.name).join(', ')}\n\nDie Nomination wird trotzdem gespeichert und die Dateien sind per E-Mail verfügbar.`);
      }

      // Keep existing GetForm submission for email notifications (always works)
      const formDataToSend = new FormData();
      files.forEach(file => {
        formDataToSend.append("files[]", file);
      });

      // Prepare referral source for GetForm
      let referralSourceForGetForm = formData.step4.referralSource || '';
      if (formData.step4.referralSource === 'Sonstiges (bitte angeben)' && formData.step4.otherReferralSource) {
        referralSourceForGetForm = `${formData.step4.referralSource}: ${formData.step4.otherReferralSource}`;
      }

      // Add all form data fields
      for (const [step, stepData] of Object.entries(formData)) {
        for (const [key, value] of Object.entries(stepData)) {
          // Skip otherReferralSource field when it's not needed
          if (key === 'otherReferralSource' && stepData.referralSource !== 'Sonstiges (bitte angeben)') {
            continue;
          }
          // Skip files field as it's handled separately
          if (key === 'files') {
            continue;
          }
          // Ensure all values are sent, including team member fields
          if (value !== null && value !== undefined && value !== '') {
            formDataToSend.append(key, value);
            // Log team member fields for debugging
            if (key.includes('teamMember')) {
              console.log(`Sending team member field to GetForm: ${key} = ${value}`);
            }
          }
        }
      }

      // Add special formatted referral source field for GetForm
      formDataToSend.append('referralSourceFormatted', referralSourceForGetForm);

      // Submit to GetForm for email notifications
      const getFormResponse = await fetch("https://getform.io/f/bnlqdwlb", {
        method: "POST",
        body: formDataToSend,
      });

      if (getFormResponse.ok) {
        console.log("GetForm submission successful");

        // Prepare data for Strapi (with files)
        const strapiFormData = new FormData();

        // Add all nomination data
        const nominationData = {
          selectedOption: formData.step1.selectedOption,
          name: formData.step2.name,
          age: parseInt(formData.step2.age),
          email: formData.step2.email,
          place: formData.step2.place,
          telephone: formData.step2.telephone || '',
          socialMediaLink: formData.step2.socialMediaLink || '',
          socialMediaLinks: formData.step2.socialMediaLinks || [],
          engagementAloneOrTeam: formData.step2.engagementAloneOrTeam,
          teamMember1Name: formData.step2.teamMember1Name || '',
          teamMember1Age: formData.step2.teamMember1Age ? parseInt(formData.step2.teamMember1Age) : null,
          teamMember1Email: formData.step2.teamMember1Email || '',
          teamMember1Telephone: formData.step2.teamMember1Telephone || '',
          teamMember2Name: formData.step2.teamMember2Name || '',
          teamMember2Age: formData.step2.teamMember2Age ? parseInt(formData.step2.teamMember2Age) : null,
          teamMember2Email: formData.step2.teamMember2Email || '',
          teamMember2Telephone: formData.step2.teamMember2Telephone || '',
          engagementDescription: formData.step3.engagementDescription,
          engagementWhy: formData.step3.engagementWhy,
          howDidYouHear: formData.step3.howDidYouHear || '',
          additionalNotes: formData.step3.additionalNotes || '',
          Nominator: formData.step4.Nominator,
          NominatorRelationship: formData.step4.NominatorRelationship,
          NominatorEmail: formData.step4.NominatorEmail,
          NominatorTelephone: formData.step4.NominatorTelephone || '',
          NominatorInstitution: formData.step4.NominatorInstitution || '',
          agencyId: formData.step4.agencyId || '',
          referralSource: formData.step4.referralSource || '',
          otherReferralSource: formData.step4.otherReferralSource || '',
          contactConsent: formData.step4.contactConsent,
          infoConsent: formData.step4.infoConsent || false,
          newsletterSubscription: formData.step4.newsletterSubscription || false
        };

        // PHONE NUMBER FIX - Handle empty telephone fields and convert + to 00 format
        // Nominator telephone
        if (!nominationData.NominatorTelephone || nominationData.NominatorTelephone === '') {
          nominationData.NominatorTelephone = '00490000000000'; // Set a default value instead of empty
        } else if (nominationData.NominatorTelephone.startsWith('+')) {
          nominationData.NominatorTelephone = '00' + nominationData.NominatorTelephone.substring(1);
        }

        // Nominee telephone (if present)
        if (nominationData.telephone && nominationData.telephone.startsWith('+')) {
          nominationData.telephone = '00' + nominationData.telephone.substring(1);
        }

        // Team member 1 telephone (if present)
        if (nominationData.teamMember1Telephone && nominationData.teamMember1Telephone.startsWith('+')) {
          nominationData.teamMember1Telephone = '00' + nominationData.teamMember1Telephone.substring(1);
        }

        // Team member 2 telephone (if present)
        if (nominationData.teamMember2Telephone && nominationData.teamMember2Telephone.startsWith('+')) {
          nominationData.teamMember2Telephone = '00' + nominationData.teamMember2Telephone.substring(1);
        }

        // Add nomination data as JSON
        strapiFormData.append('data', JSON.stringify(nominationData));

        // Only add files that are small enough for Airtable
        const uploadableFiles = files.filter(file => file.size <= MAX_AIRTABLE_SIZE);
        uploadableFiles.forEach(file => {
          strapiFormData.append('files', file);
        });

        // Submit to Strapi which will pass to Airtable
        const strapiBaseUrl = process.env.GATSBY_STRAPI_API_URL || 'https://allianzfoundation.herokuapp.com';
        const strapiToken = process.env.GATSBY_STRAPI_API_TOKEN;

        // ADD AUTHORIZATION HEADER HERE
        const strapiResponse = await fetch(`${strapiBaseUrl}/api/mot-nominations/submit`, {
          method: "POST",
          headers: {
            'Authorization': `Bearer ${strapiToken}`
          },
          body: strapiFormData
        });

        const strapiResult = await strapiResponse.json();

        if (strapiResponse.ok) {
          // Also submit nominee data
          const nomineeData = {
            name: formData.step2.name,
            age: parseInt(formData.step2.age),
            email: formData.step2.email,
            place: formData.step2.place,
            telephone: formData.step2.telephone || '',
            socialMediaLink: formData.step2.socialMediaLink || '',
            socialMediaLinks: formData.step2.socialMediaLinks || [],
            engagementAloneOrTeam: formData.step2.engagementAloneOrTeam,
            teamMember1Name: formData.step2.teamMember1Name || '',
            teamMember1Age: formData.step2.teamMember1Age ? parseInt(formData.step2.teamMember1Age) : null,
            teamMember2Name: formData.step2.teamMember2Name || '',
            teamMember2Age: formData.step2.teamMember2Age ? parseInt(formData.step2.teamMember2Age) : null,
            nominationId: strapiResult.id
          };

          // Fix phone format for nominee data as well
          if (nomineeData.telephone && nomineeData.telephone.startsWith('+')) {
            nomineeData.telephone = '00' + nomineeData.telephone.substring(1);
          }

          // Send nominee data to second GetForm endpoint
          const nomineeFormData = new FormData();
          for (const [key, value] of Object.entries(nomineeData)) {
            if (value !== null && value !== undefined) {
              nomineeFormData.append(key, value);
            }
          }

          try {
            await fetch("https://getform.io/f/broyzwxa", {
              method: "POST",
              body: nomineeFormData,
            });
            console.log("Nominee data sent to second GetForm endpoint");
          } catch (nomineeFormError) {
            console.error("Error sending nominee data to GetForm:", nomineeFormError);
          }

          // ADD AUTHORIZATION HEADER HERE FOR NOMINEE ENDPOINT
          await fetch(`${strapiBaseUrl}/api/mot-nominees/submit`, {
            method: "POST",
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${strapiToken}`
            },
            body: JSON.stringify(nomineeData)
          });

          navigate(`/${data.strapiMotSetting.NominationSuccessPage.subpageSlug}`);
        } else {
          throw new Error("Strapi submission failed");
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Form submission failed. Please try again or contact support.");
    }
  };

  const handleEdit = (step, data) => {
    setFormData((prev) => ({ ...prev, [`step${step}`]: data }));
    setCurrentStep(step);
  };

  const closeDialog = () => {
    setDialogVisible(false);
    if (resetOption) {
      resetOption(); // Call reset function if it exists
    }
    if (isSuccess) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const triggerDialog = (message, success = false) => {
    setDialogTitle(success ? "Super!" : "Hinweis: Leider können wir nur nominierte Personen akzeptieren, die sich in einem der vorher beschriebenen Bereiche engagieren.");
    setDialogMessage(message);
    setIsSuccess(success);
    setDialogVisible(true);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1 next={handleNext} abort={abort} data={formData.step1} triggerDialog={triggerDialog} setResetOption={setResetOption} />;
      case 2:
        return <Step2 next={handleNext} back={handleBack} data={formData.step2} />;
      case 3:
        return <Step3 next={handleNext} back={handleBack} data={formData.step3} files={files} setFiles={setFiles} fileError={fileError} />;
      case 4:
        return <Step4 next={handleSubmit} back={handleBack} data={formData.step4} />;
      case 5:
        return (
          <Summary
            data={formData}
            edit={handleEdit}
            finalSubmit={handleFinalSubmit}
            back={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <Header data={data} />
      <section className="container mx-auto max-w-8xl mt-32 md:mt-40 mb-48 md:mb-64">
        <article className="text-white text-center mb-20">
          <h1 className="text-3xl md:text-5xl hyphens-auto mb-12" lang="de">Nominierungsformular</h1>
        </article>
        <article className="nomination-form container mx-auto flex flex-col gap-16 md:gap-32 max-w-2xl px-4 md:px-0">
          <div>
            <ProgressIndicator currentStep={currentStep} />
          </div>
          <div>
            {renderStep()}
          </div>
        </article>
      </section>
      <CustomDialog
        isOpen={dialogVisible}
        onClose={closeDialog}
        title={dialogTitle}
        message={dialogMessage}
        isSuccess={isSuccess}
        onProceed={closeDialog}
      />
    </Layout>
  );
}

export const query = graphql`
  query {
    strapiMotSetting {
       countdown
       countdownDate
       countdownText
       headerBtn
       headerBtnLink
       headerBtnText
       nomination
       voting
       id
       partnerHeadline
       partnerDescription
       FooterMenu {
         subpageSlug
         subpageTitle
       }
       NominationSuccessPage {
         subpageSlug
         subpageTitle
       }
       UploadSuccessPage {
         subpageSlug
         subpageTitle
       }
       Partner {
         galeryDesc
         galeryHeadline
         id
         galeryOverlayText
         galeryMedia {
           caption
           alternativeText
           mime
           name
           url
           file {
              childImageSharp {
                gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
           }
         }
       }
     }
   }
`;

export default Invite;

export function Head() {
  return (
    <>
      <html lang="de" />
      <title>Nominierungsformular &mdash; Movers of Tomorrow Award</title>
      <meta name="description" content="Morgen fängt heute an! Der Movers of Tomorrow Award, der Engagementpreis der Allianz Foundation, feiert junge Menschen, die sich für eine bessere Zukunft einsetzen." />
      <meta name="image" content="https://res.cloudinary.com/allianz-foundation/image/upload/v1720102007/Moversof_Tomorrow_og_image_5a49773177.png" />
      <meta property="og:title" content="Nomination &mdash; Movers of Tomorrow Award" />
      <meta property="og:description" content="Morgen fängt heute an! Der Movers of Tomorrow Award, der Engagementpreis der Allianz Foundation, feiert junge Menschen, die sich für eine bessere Zukunft einsetzen." />
      <meta property="og:image" content="https://res.cloudinary.com/allianz-foundation/image/upload/v1720102007/Moversof_Tomorrow_og_image_5a49773177.png" />
      <meta name="twitter:title" content="Nomination &mdash; Movers of Tomorrow Award" />
      <meta name="twitter:description" content="Morgen fängt heute an! Der Movers of Tomorrow Award, der Engagementpreis der Allianz Foundation, feiert junge Menschen, die sich für eine bessere Zukunft einsetzen." />
      <meta name="twitter:image" content="https://res.cloudinary.com/allianz-foundation/image/upload/v1720102007/Moversof_Tomorrow_og_image_5a49773177.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="robots" content="noindex,nofollow" />
      <meta name="googlebot" content="noindex,nofollow" />
    </>
  );
}
